import React, { useMemo, useState } from 'react';

import {
  PickerEmpty,
  PickerFooterAction,
  PickerHeading,
  PickerList,
  PickerMenu,
  PickerOption,
  PickerPopover,
  PickerSearch,
} from './PickerParts';

export interface PickerItem<TValue extends string> {
  value: TValue;
  label: string;
  group?: string;
  leading?: React.ReactNode;
  labelAdornment?: React.ReactNode;
  trailing?: React.ReactNode;
  disabled?: boolean;
}

interface PickerBaseProps<TValue extends string> {
  trigger: React.ReactNode;
  options: PickerItem<TValue>[];
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchLabel?: string;
  emptyLabel?: string;
  noneOption?: {
    label: string;
    leading?: React.ReactNode;
    onSelect: () => void;
  };
  footerAction?: { label: string; icon?: React.ReactNode; onClick: () => void };
  width?: number;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  anchorToRowEnd?: boolean;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  contentClassName?: string;
  listClassName?: string;
}

interface PickerSingleProps<TValue extends string>
  extends PickerBaseProps<TValue> {
  selection?: 'single';
  value?: TValue | null;
  onSelect: (value: TValue) => void;
}

interface PickerMultiProps<TValue extends string>
  extends PickerBaseProps<TValue> {
  selection: 'multi';
  values: TValue[];
  onChange: (values: TValue[]) => void;
}

export type PickerProps<TValue extends string> =
  | PickerSingleProps<TValue>
  | PickerMultiProps<TValue>;

export function Picker<TValue extends string>(props: PickerProps<TValue>) {
  const {
    trigger,
    options,
    showSearch = true,
    searchPlaceholder = 'Search...',
    searchLabel,
    emptyLabel = 'No results',
    noneOption,
    footerAction,
    width,
    align = 'start',
    side,
    anchorToRowEnd = false,
    disabled = false,
    open,
    onOpenChange,
    contentClassName,
    listClassName,
  } = props;

  const [search, setSearch] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const isMultiSelect = props.selection === 'multi';
  const selectedValues = new Set(
    props.selection === 'multi'
      ? props.values
      : props.value == null
        ? []
        : [props.value]
  );

  const query = search.trim().toLowerCase();
  const groups = useMemo(() => {
    const matches =
      query === ''
        ? options
        : options.filter((option) =>
            option.label.toLowerCase().includes(query)
          );

    const grouped: { label?: string; options: PickerItem<TValue>[] }[] = [];
    matches.forEach((option) => {
      const openGroup = grouped[grouped.length - 1];
      if (openGroup && openGroup.label === option.group) {
        openGroup.options.push(option);
        return;
      }
      grouped.push({ label: option.group, options: [option] });
    });

    return grouped;
  }, [options, query]);

  const handleOpenChange = (nextOpen: boolean) => {
    setIsPickerOpen(nextOpen);
    if (!nextOpen) setSearch('');
    onOpenChange?.(nextOpen);
  };

  const handleSelect = (value: TValue) => {
    if (props.selection === 'multi') {
      props.onChange(
        props.values.includes(value)
          ? props.values.filter((selected) => selected !== value)
          : [...props.values, value]
      );
      return;
    }

    props.onSelect(value);
    handleOpenChange(false);
  };

  return (
    <PickerPopover
      trigger={trigger}
      align={align}
      side={side}
      disabled={disabled}
      anchorToRowEnd={anchorToRowEnd}
      contentClassName={contentClassName}
      open={open ?? isPickerOpen}
      onOpenChange={handleOpenChange}
    >
      <PickerMenu width={width}>
        {showSearch ? (
          <PickerSearch
            value={search}
            onChange={setSearch}
            placeholder={searchPlaceholder}
            label={searchLabel ?? searchPlaceholder}
          />
        ) : null}

        <PickerList className={listClassName}>
          {noneOption && query === '' ? (
            <PickerOption
              label={noneOption.label}
              leading={noneOption.leading}
              isSelected={!isMultiSelect && selectedValues.size === 0}
              onSelect={() => {
                noneOption.onSelect();
                handleOpenChange(false);
              }}
            />
          ) : null}

          {groups.map((group) => (
            <div key={group.options[0].value} className="flex w-full flex-col">
              {group.label ? (
                <PickerHeading>{group.label}</PickerHeading>
              ) : null}
              {group.options.map((option) => (
                <PickerOption
                  key={option.value}
                  label={option.label}
                  leading={option.leading}
                  labelAdornment={option.labelAdornment}
                  trailing={option.trailing}
                  disabled={option.disabled}
                  checked={
                    isMultiSelect ? selectedValues.has(option.value) : undefined
                  }
                  isSelected={
                    !isMultiSelect && selectedValues.has(option.value)
                  }
                  onSelect={() => handleSelect(option.value)}
                />
              ))}
            </div>
          ))}

          {groups.length === 0 ? <PickerEmpty>{emptyLabel}</PickerEmpty> : null}
        </PickerList>

        {footerAction ? (
          <PickerFooterAction
            label={footerAction.label}
            icon={footerAction.icon}
            onClick={footerAction.onClick}
          />
        ) : null}
      </PickerMenu>
    </PickerPopover>
  );
}
